import { useQuery } from '@tanstack/react-query';
import { type SortingState, useTable } from '@tanstack/react-table';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { DamageType } from '@/common/damage';
import { DataTable } from '@/components/data-table/data-table';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	DROPDOWN_NAMES,
	getDropdownOptionByDropdown,
	useDamageTypeOptions,
	useMaterialOptions,
} from '@/hooks/use-dropdowns';
import { getListDamagesOptions } from '@/queries/damages/list-damages';
import {
	type DamagePickerTableMeta,
	damagePickerColumns,
	damagePickerTableFeatures,
	sortPickerDamages,
} from './damage-picker-columns';

export type DamageListDialogProps = {
	onDamageSelect?: (damages: DamageType[]) => void;
	open?: boolean;
	setOpen?: (open: boolean) => void;
	isMultiSelect?: boolean;
	alreadyAddedDamagesIds?: Set<string>;
};
export function DamageListDialog({
	onDamageSelect,
	open,
	setOpen,
	isMultiSelect = false,
	alreadyAddedDamagesIds,
}: DamageListDialogProps) {
	const { t } = useTranslation();
	const { assetId } = useParams<{ assetId: string }>();
	const [filters, setFilters] = React.useState({
		inspection: 'all',
		class: 'all',
		materialId: 'all',
		subAsset: 'all',
	});
	const [selectedDamagesIds, setSelectedDamagesIds] = React.useState(new Set<string>());
	const [sorting, setSorting] = React.useState<SortingState>([]);
	const [showConfirmationAlert, setShowConfirmationAlert] = React.useState(false);
	const { isLoading: isOptionsLoading, options } = getDropdownOptionByDropdown(DROPDOWN_NAMES.DAMAGE_STATUSES);
	const defaultStatusIds =
		options?.filter?.((opt) => opt.value !== 'INSTANDGESETZT').map((opt) => opt.id.toString()) || [];
	const { materialsGroupByCategory, materialMap } = useMaterialOptions();
	const { damageOptionsMap } = useDamageTypeOptions();
	const { data: listDamageResponse } = useQuery({
		...getListDamagesOptions(
			{
				assetId,
				offset: 0,
				limit: 300,
				statusIds: defaultStatusIds.join(','),
			},
			!!assetId && !isOptionsLoading && !!options.length,
		),
	});
	const damages = listDamageResponse?.damages || [];

	React.useEffect(() => {
		if (open === false) {
			setFilters({
				inspection: 'all',
				class: 'all',
				materialId: 'all',
				subAsset: 'all',
			});
			setSelectedDamagesIds(new Set<string>());
			setSorting([]);
		}
	}, [open]);

	const onSubmit = () => {
		const selectedDamages = damages.filter((row) => selectedDamagesIds.has(row.id as string));
		if (onDamageSelect) {
			onDamageSelect(selectedDamages);
		}
	};

	// Filter damages for association dialog
	const filteredDamages = React.useMemo(
		() =>
			damages.filter((damage) => {
				if (alreadyAddedDamagesIds?.has(damage.id as string)) return false;
				if (filters.inspection !== 'all' && damage.inspectionId !== filters.inspection) return false;
				if (filters.class !== 'all' && damage.class !== filters.class) return false;
				if (filters.materialId !== 'all' && damage.materialId !== Number(filters.materialId)) return false;
				if (filters.subAsset !== 'all' && damage.subAssetId !== filters.subAsset) return false;
				return true;
			}),
		[damages, filters, alreadyAddedDamagesIds],
	);

	const labels = React.useMemo<Pick<DamagePickerTableMeta, 'damageTypeLabel' | 'materialLabel'>>(
		() => ({
			damageTypeLabel: (damage) => damageOptionsMap?.[damage.damageTypeId as number]?.label,
			materialLabel: (damage) => materialMap?.[damage.materialId as number]?.label,
		}),
		[damageOptionsMap, materialMap],
	);
	// The API returns its default order (newest first), the same 300 rows whatever the sort.
	const sortedDamages = React.useMemo(
		() => sortPickerDamages(filteredDamages, sorting, labels),
		[filteredDamages, sorting, labels],
	);

	const filtersListData = React.useMemo(() => {
		const tempObj = {
			inspection: {} as Record<string, boolean>,
			class: {} as Record<string, boolean>,
			subAsset: {} as Record<string, boolean>,
		};
		const filtersData = {
			inspections: [] as Array<{ label: string; value: string }>,
			classes: [] as Array<{ label: string; value: string }>,
			subAssets: [] as Array<{ label: string; value: string }>,
		};
		damages.forEach((damage) => {
			if (damage.inspection?.id && !tempObj.inspection[damage.inspection.id]) {
				tempObj.inspection[damage.inspection.id] = true;
				filtersData.inspections.push({ label: damage.inspection?.title || '', value: damage.inspection.id });
			}
			if (damage.class && !tempObj.class[damage.class]) {
				tempObj.class[damage.class] = true;
				filtersData.classes.push({ label: damage.class, value: damage.class });
			}
			if (damage.subAsset?.id && !tempObj.subAsset[damage.subAsset.id]) {
				tempObj.subAsset[damage.subAsset.id] = true;
				filtersData.subAssets.push({ label: damage.subAsset.name, value: damage.subAsset.id });
			}
		});
		return filtersData;
	}, [damages]);
	const filterValueChange = (filterId: keyof typeof filters) => (value: string) => {
		setFilters((prev) => ({ ...prev, [filterId]: value }));
	};
	const toggleDamageSelection = (damageId: string) => {
		const newSelection = new Set(selectedDamagesIds);
		if (newSelection.has(damageId)) {
			newSelection.delete(damageId);
		} else {
			newSelection.add(damageId);
		}
		setSelectedDamagesIds(newSelection);
	};
	const handleDamageSelect = (damage: DamageType) => {
		const newSet = new Set<string>();
		newSet.add(damage.id as string);
		setSelectedDamagesIds(newSet);
		setShowConfirmationAlert(true);
	};
	const table = useTable({
		features: damagePickerTableFeatures,
		columns: damagePickerColumns,
		data: sortedDamages,
		getRowId: (row) => row.id as string,
		state: {
			sorting,
			// Checkboxes for multi-select, a "Select" button for single-select.
			columnVisibility: { select: isMultiSelect, selectAction: !isMultiSelect },
		},
		onSortingChange: setSorting,
		// Rows arrive sorted (`sortPickerDamages`); the table only tracks the sort state for the headers.
		manualSorting: true,
		// Skip auto-detecting the first direction from row values; always start ascending.
		sortDescFirst: false,
		meta: {
			...labels,
			isSelected: (damage) => selectedDamagesIds.has(damage.id as string),
			onToggle: (damage) => toggleDamageSelection(damage.id as string),
		},
	});
	const onConfirmation = () => {
		const damage = damages.find((d) => selectedDamagesIds.has(d.id as string));
		if (!damage) return;
		onDamageSelect?.([damage]);
		setShowConfirmationAlert(false);
	};
	return (
		<>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-4xl! max-h-[80vh]!">
					<DialogHeader>
						<DialogTitle>{t('finance.damages.select.title')}</DialogTitle>
					</DialogHeader>
					<div className="grid grid-cols-4 gap-3">
						<div>
							<Label className="block text-xs font-medium text-muted-foreground mb-1">
								{t('finance.damages.select.filters.inspection')}
							</Label>
							<Select value={filters.inspection as string} onValueChange={filterValueChange('inspection')}>
								<SelectTrigger className="bg-input h-8 text-sm w-full">
									<SelectValue placeholder={t('common.states.all')} />
								</SelectTrigger>
								<SelectContent position="popper">
									<SelectItem value="all">{t('common.states.all')}</SelectItem>
									{filtersListData.inspections.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label className="block text-xs font-medium text-muted-foreground mb-1">
								{t('finance.damages.class')}
							</Label>
							<Select value={filters.class as string} onValueChange={filterValueChange('class')}>
								<SelectTrigger className="bg-input h-8 text-sm w-full">
									<SelectValue placeholder={t('common.states.all')} />
								</SelectTrigger>
								<SelectContent position="popper">
									<SelectItem value="all">{t('common.states.all')}</SelectItem>
									{filtersListData.classes.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label className="block text-xs font-medium text-muted-foreground mb-1">
								{t('finance.damages.material')}
							</Label>
							<Select value={filters.materialId as string} onValueChange={filterValueChange('materialId')}>
								<SelectTrigger className="bg-input h-8 text-sm w-full">
									<SelectValue placeholder={t('common.states.all')} />
								</SelectTrigger>
								<SelectContent position="popper">
									<SelectItem value="all">{t('common.states.all')}</SelectItem>
									<SelectContent className="max-h-75">
										{Object.entries(materialsGroupByCategory || {}).map(([group, options]) => (
											<SelectGroup key={group}>
												<SelectLabel className="sticky top-0 z-10 bg-muted text-foreground text-xs uppercase tracking-wider pl-2 py-1 font-extrabold shadow-sm border-b border-border">
													{group}
												</SelectLabel>
												{options.map((opt) => (
													<SelectItem key={opt.id} value={String(opt.id)}>
														{opt.label}
													</SelectItem>
												))}
											</SelectGroup>
										))}
									</SelectContent>
								</SelectContent>
							</Select>
						</div>
						<div>
							<Label className="block text-xs font-medium text-muted-foreground mb-1">
								{t('finance.damages.assetSubAsset')}
							</Label>
							<Select value={filters.subAsset as string} onValueChange={filterValueChange('subAsset')}>
								<SelectTrigger className="bg-input h-8 text-sm w-full">
									<SelectValue placeholder={t('common.states.all')} />
								</SelectTrigger>
								<SelectContent position="popper">
									<SelectItem value="all">{t('common.states.all')}</SelectItem>
									{filtersListData.subAssets.map((option) => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div className="overflow-auto max-h-100">
						<DataTable
							table={table}
							tableClassName="caption-bottom text-sm"
							onRowClick={(row) => {
								if (isMultiSelect) {
									toggleDamageSelection(row.id);
								} else {
									handleDamageSelect(row.original);
								}
							}}
							// Selected rows need their own hover colour, otherwise TableRow's hover:bg-muted/50 hides the highlight.
							getRowClassName={(row) =>
								isMultiSelect &&
								selectedDamagesIds.has(row.id) &&
								'bg-blue-50 hover:bg-blue-100 dark:bg-accent dark:hover:bg-accent'
							}
							emptyState={<span className="block py-4 text-muted-foreground">{t('finance.damages.select.empty')}</span>}
						/>
					</div>
					<DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
						<DialogClose asChild>
							<Button
								variant="outline"
								onClick={() => {
									setOpen?.(false);
								}}
							>
								{t('common.actions.cancel')}
							</Button>
						</DialogClose>
						{isMultiSelect && (
							<Button onClick={onSubmit} disabled={selectedDamagesIds.size === 0}>
								{t('finance.damages.select.addSelected', { count: selectedDamagesIds.size })}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>
			<AlertDialog open={showConfirmationAlert} onOpenChange={setShowConfirmationAlert}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('finance.damages.linkConfirm.title')}</AlertDialogTitle>
						<AlertDialogDescription>{t('finance.damages.linkConfirm.description')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.states.no')}</AlertDialogCancel>
						<AlertDialogAction onClick={onConfirmation}>{t('common.states.yes')}</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
